import { createEffect } from 'effector';
import { useStore } from 'effector-react';
import { ButtonContainer } from 'features/fallback/ui/button-container';
import { StatusBlock } from 'features/fallback/ui/info-block';
import { Input } from 'features/fallback/ui/input';
import { validateEmail } from 'libs/validate-email';
import { css } from 'linaria';
import { styled } from 'linaria/react';
import { sendEvent } from 'model/helpers/events.model';
import { createLinkFactory } from 'model/flows/fallback.model';
import { http } from 'model/helpers/http';
import { screenHistoryPush, ScreenName } from 'model/helpers/screens-history';
import { useLocalization } from 'model/localization.model';
import { Fragment, h } from 'preact';
import { useEffect, useState } from 'preact/hooks';

import { Button } from 'ui/atoms/button';
import { Container } from 'ui/container';
import { EmailIcon } from 'ui/icons/email-icon';
import { PlaneIcon } from 'ui/icons/plane-icon';
import { ReloadIcon } from 'ui/icons/reload-icon';
import { Spinner } from 'ui/spinner';
import { Subtitle, Text } from 'ui/typography';

const link = createLinkFactory(
	createEffect({
		handler: async ({ value, sessionId }) => {
			await http.post({
				url: '/cross_device/send_email',
				body: {
					session_id: sessionId,
					email: value,
				},
			});
		},
	}),
);

export const LinkEmailView = () => {
	const t = useLocalization();
	const value = useStore(link.value$);
	const isSuccess = useStore(link.isSuccess$);
	const isEmailSending = useStore(link.sendFx.pending);
	const [email, updateEmail] = useState(value);
	const [isValidEmail, updateIsValidEmail] = useState(false);
	const [start] = useState(Date.now());

	const handleSendAgain = (e: Event) => {
		e.preventDefault();
		// @ts-ignore
		if (e.target.tagName.toLowerCase() !== 'a') { return; }

		link.sendFx(email);
		sendEvent({ event: 'email_success_send_again' });
	};
	const handleTryAgain = () => {
		link.tryAgain();
		sendEvent({ event: 'email_error_try_again' });
	};
	const handleUseDifferent = () => {
		link.useDifferent();
		sendEvent({ event: 'email_success_use_different' });
	};
	const handleSendEmail = () => {
		sendEvent({
			event: 'email_send_clicked',
			event_attributes: {
				duration: Date.now() - start,
			},
		});
		link.sendFx(email);
	};

	useEffect(() => {
		const isValid = validateEmail(email);
		updateIsValidEmail(isValid);
	}, [email]);
	useEffect(() => {
		setTimeout(() => {
			sendEvent({ event: 'email_screen_shown' });
		}, 20);
	}, []);
	useEffect(() => {
		if (isSuccess === null) {
			screenHistoryPush(ScreenName.email);
		}
		if (isSuccess === true) {
			screenHistoryPush(ScreenName.email_success);
			setTimeout(() => {
				sendEvent({ event: 'email_success_screen_shown' });
			}, 20);
		}
		if (isSuccess === false) {
			screenHistoryPush(ScreenName.email_error);
			setTimeout(() => {
				sendEvent({ event: 'email_error_screen_shown' });
			}, 20);
		}
	}, [isSuccess]);

	return (
		<Container>
			<Subtitle>{t('email.title')}</Subtitle>
			<Text className={textClass}>{t('email.description')}</Text>
			{isSuccess === false && (
				<Fragment>
					<StatusBlock status={'warning'}>
						{t('email.error.msg')}
					</StatusBlock>
					<Button
						name={'email.btn.error'}
						type={'link'}
						className={buttonClass}
						style={{
							marginBottom: 32,
						}}
						onClick={handleTryAgain}
						icon={
							<ReloadIcon/>
						}
					>
						{t('email.btn.error')}
					</Button>
				</Fragment>
			)}
			{isSuccess === true && (
				<SentSuccess data-loading={isEmailSending}>
					<StatusBlock status={'success'}>
						{t('email.success.msg', {
							email: `<strong>${email}</strong>`,
						})}
					</StatusBlock>
					<Text
						onClick={handleSendAgain}
						style={{ textAlign: 'center', marginTop: 8 }}
						dangerouslySetInnerHTML={{
							__html: t('email.success.hint', {
								'email.success.hint.sub': `
									<a href="#">
										${t('email.success.hint.sub')}
									</a>`,
							}),
						}}
					/>
					<Button
						name={'email.btn.success'}
						type={'link'}
						className={buttonClass}
						onClick={handleUseDifferent}
						icon={
							<ReloadIcon fill={'currentColor'}/>
						}
					>
						{t('email.btn.success')}
					</Button>
					{isEmailSending && (
						<Spinner
							size={32}
							className={spinner}
							fill={{
								primary: 'var(--primary-color)',
								secondary: 'var(--secondary-color)',
							}}
						/>
					)}
				</SentSuccess>
			)}
			{isSuccess === null && (
				<Fragment>
					<Input
						autofocus={true}
						value={email}
						icon={EmailIcon}
						onChange={e => updateEmail(e.target.value)}
						placeholder={'email@example.com'}
					/>
					<ButtonContainer style={{ marginTop: 16, width: '100%' }}>
						<Button
							name={'email.btn'}
							onClick={handleSendEmail}
							loading={isEmailSending}
							icon={<PlaneIcon/>}
							disabled={!isValidEmail}
							style={{ width: '100%'  }}
						>
							{t('email.btn')}
						</Button>
					</ButtonContainer>
				</Fragment>
			)}
		</Container>
	);
};

const SentSuccess = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;

  &[data-loading='true'] {
    & > * {
      opacity: .5;
    }
  }
`;
const textClass = css`
  margin-bottom: 20px;
  min-height: 44px;
`;
const buttonClass = css`
  margin-top: 8px;
  width: 100%;
`;
const spinner = css`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  animation: spin 1s ease-in-out infinite;
`;
